// FreightProof IndexedDB — offline-first data storage
// Stores: vehicles, trailers, inspections, drivers, trips, photos, sync-queue

import { openDB } from 'idb';

const DB_NAME = 'freightproof';
const DB_VERSION = 1;

let db = null;

export async function initDB() {
  db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      // ─── VEHICLES (trucks, by VIN) ────────────────────────────
      if (!database.objectStoreNames.contains('vehicles')) {
        const vs = database.createObjectStore('vehicles', { keyPath: 'vin' });
        vs.createIndex('updatedAt', 'updatedAt');
        vs.createIndex('carrierDOT', 'carrierDOT');
      }

      // ─── TRAILERS (by serialNumber) ───────────────────────────
      if (!database.objectStoreNames.contains('trailers')) {
        const ts = database.createObjectStore('trailers', { keyPath: 'id' });
        ts.createIndex('updatedAt', 'updatedAt');
        ts.createIndex('carrierDOT', 'carrierDOT');
      }

      // ─── INSPECTIONS ──────────────────────────────────────────
      if (!database.objectStoreNames.contains('inspections')) {
        const is = database.createObjectStore('inspections', { keyPath: 'id' });
        is.createIndex('vehicleId', 'vehicleId');
        is.createIndex('timestamp', 'timestamp');
        is.createIndex('type', 'type');
      }

      // ─── DRIVERS ──────────────────────────────────────────────
      if (!database.objectStoreNames.contains('drivers')) {
        const ds = database.createObjectStore('drivers', { keyPath: 'id' });
        ds.createIndex('carrierDOT', 'carrierDOT');
        ds.createIndex('cdlNumber', 'cdlNumber');
      }

      // ─── TRIPS ────────────────────────────────────────────────
      if (!database.objectStoreNames.contains('trips')) {
        const trs = database.createObjectStore('trips', { keyPath: 'id' });
        trs.createIndex('timestamp', 'timestamp');
        trs.createIndex('vehicleId', 'vehicleId');
        trs.createIndex('driverId', 'driverId');
      }

      // ─── PHOTOS ───────────────────────────────────────────────
      if (!database.objectStoreNames.contains('photos')) {
        database.createObjectStore('photos', { keyPath: 'id' });
      }

      // ─── SYNC QUEUE ───────────────────────────────────────────
      if (!database.objectStoreNames.contains('syncQueue')) {
        const sq = database.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
        sq.createIndex('createdAt', 'createdAt');
      }
    },
  });
  return db;
}

export function getDB() { return db; }

// ─── VEHICLES ───────────────────────────────────────────────────────

export async function addVehicle(vehicle) {
  vehicle.updatedAt = Date.now();
  vehicle.createdAt = vehicle.createdAt || Date.now();
  await db.put('vehicles', vehicle);
  return vehicle;
}

export async function getVehicle(vin) {
  return db.get('vehicles', vin);
}

export async function getAllVehicles(carrierDOT) {
  if (carrierDOT) {
    return db.getAllFromIndex('vehicles', 'carrierDOT', carrierDOT);
  }
  return db.getAll('vehicles');
}

export async function deleteVehicle(vin) {
  await db.delete('vehicles', vin);
}

// ─── TRAILERS ───────────────────────────────────────────────────────

export async function addTrailer(trailer) {
  trailer.id = trailer.id || `T-${trailer.serialNumber || trailer.plateNumber || Date.now()}`;
  trailer.updatedAt = Date.now();
  trailer.createdAt = trailer.createdAt || Date.now();
  await db.put('trailers', trailer);
  return trailer;
}

export async function getTrailer(id) {
  return db.get('trailers', id);
}

export async function getAllTrailers(carrierDOT) {
  if (carrierDOT) {
    return db.getAllFromIndex('trailers', 'carrierDOT', carrierDOT);
  }
  return db.getAll('trailers');
}

// ─── INSPECTIONS ────────────────────────────────────────────────────

export async function addInspection(inspection) {
  inspection.id = inspection.id || `INS-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  inspection.timestamp = inspection.timestamp || Date.now();
  await db.put('inspections', inspection);
  return inspection;
}

export async function getInspectionsForVehicle(vehicleId, limit = 50) {
  const all = await db.getAllFromIndex('inspections', 'vehicleId', vehicleId);
  return all.sort((a, b) => b.timestamp - a.timestamp).slice(0, limit);
}

export async function getAllInspections(limit = 100) {
  const all = await db.getAll('inspections');
  return all.sort((a, b) => b.timestamp - a.timestamp).slice(0, limit);
}

// ─── DRIVERS ────────────────────────────────────────────────────────

export async function addDriver(driver) {
  driver.id = driver.id || `DRV-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  driver.updatedAt = Date.now();
  driver.createdAt = driver.createdAt || Date.now();
  await db.put('drivers', driver);
  return driver;
}

export async function getDriver(id) {
  return db.get('drivers', id);
}

export async function getAllDrivers(carrierDOT) {
  if (carrierDOT) {
    return db.getAllFromIndex('drivers', 'carrierDOT', carrierDOT);
  }
  return db.getAll('drivers');
}

// ─── TRIPS ──────────────────────────────────────────────────────────

export async function addTrip(trip) {
  trip.id = trip.id || `TRIP-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  trip.timestamp = trip.timestamp || Date.now();
  await db.put('trips', trip);
  return trip;
}

export async function getAllTrips(limit = 50) {
  const all = await db.getAll('trips');
  return all.sort((a, b) => b.timestamp - a.timestamp).slice(0, limit);
}

// ─── PHOTOS ─────────────────────────────────────────────────────────

export async function savePhoto(id, blob) {
  await db.put('photos', { id, blob, timestamp: Date.now() });
}

export async function getPhoto(id) {
  return db.get('photos', id);
}

// ─── SYNC QUEUE ─────────────────────────────────────────────────────

export async function enqueueSync(item) {
  item.createdAt = Date.now();
  await db.add('syncQueue', item);
}

export async function getPendingSync() {
  return db.getAll('syncQueue');
}

export async function clearSyncItem(id) {
  await db.delete('syncQueue', id);
}

export async function flushSyncQueue() {
  const pending = await getPendingSync();
  let synced = 0;
  for (const item of pending) {
    try {
      const res = await fetch(item.url, {
        method: item.method || 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item.body),
      });
      if (res.ok) {
        await clearSyncItem(item.id);
        synced++;
      }
    } catch (e) {
      // Offline or failed — keep in queue
    }
  }
  return synced;
}

// Auto-flush on connectivity
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => flushSyncQueue());
}
